import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { ContentfulCardFragment } from '@/graphql/contentful/api';
import { getBrandConfig } from '@/lib/configuration/configuration';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme/getTheme';

import {
  BASE_CARD_SIZES,
  getContentAlignment,
  getTextAlignment,
  SECTION_BANNER_CARD_SIZES,
  visibilityControl,
} from '../../Card.styles';

export const Wrapper = styled(Flex)<
  Pick<
    ContentfulCardFragment,
    | 'contentAlignment'
    | 'mediaAnimationEffect'
    | 'visibleInDesktop'
    | 'visibleInMobile'
  > & {
    hasBackgroundImage: boolean;
    hasWrapperMinHeight?: boolean;
    heroCard: boolean;
    sectionBannerCard?: boolean;
    type: string | undefined | null;
  }
>`
  position: relative;
  overflow: hidden;
  height: 100%;
  color: ${({ hasBackgroundImage, theme }) =>
    hasBackgroundImage ? theme.colors.white : theme.colors.black};
  min-height: ${({ hasWrapperMinHeight, sectionBannerCard }) =>
    hasWrapperMinHeight
      ? sectionBannerCard
        ? SECTION_BANNER_CARD_SIZES.xs
        : BASE_CARD_SIZES.xs
      : ''};
  padding: ${({ hasWrapperMinHeight, theme }) =>
    hasWrapperMinHeight ? theme.spacing(8, 2) : theme.spacing(0, 2, 2, 2)};

  ${({ mediaAnimationEffect }) =>
    mediaAnimationEffect &&
    css`
      &:hover {
        cursor: pointer;
        img {
          transform: scale(1.1);
          transition: all 0.5s linear;
        }
      }
    `}

  ${({ theme, type }) =>
    type === 'MediaBanner' &&
    css`
      ${theme.media.sm.down} {
        width: 100%;
        aspect-ratio: 4 / 4.5;
      }
    `}

  ${({
    contentAlignment,
    hasWrapperMinHeight,
    heroCard,
    sectionBannerCard,
    theme,
  }) => css`
    ${theme.media.sm.up} {
      min-height: ${hasWrapperMinHeight
        ? sectionBannerCard
          ? SECTION_BANNER_CARD_SIZES.sm
          : BASE_CARD_SIZES.sm
        : ''};
    }

    ${theme.media.md.up} {
      min-height: ${hasWrapperMinHeight
        ? sectionBannerCard
          ? SECTION_BANNER_CARD_SIZES.md
          : BASE_CARD_SIZES.md
        : ''};
      padding: ${({ theme }) =>
        `${
          hasWrapperMinHeight ? theme.spacing(5) : theme.spacing(3)
        } 5% ${theme.spacing(5)}`};

      ${getContentAlignment(contentAlignment)};
    }

    ${theme.media.lg.up} {
      min-height: ${hasWrapperMinHeight
        ? sectionBannerCard
          ? SECTION_BANNER_CARD_SIZES.lg
          : BASE_CARD_SIZES.lg
        : ''};
      padding-inline: 10%;
    }

    ${theme.media.md.down} {
      padding: ${hasWrapperMinHeight
        ? theme.components.cmsCard.baseCard.mobile.paddingHasWrapperMinHeight
        : theme.components.cmsCard.baseCard.mobile.padding};
      ${heroCard
        ? css`
            justify-content: center;
            align-items: flex-end;
          `
        : getContentAlignment(contentAlignment)}
    }
  `};

  ${({ visibleInDesktop, visibleInMobile }) =>
    visibilityControl(visibleInDesktop, visibleInMobile)};
`;

export const Content = styled(Flex)<
  Pick<ContentfulCardFragment, 'textAlignment'> & {
    deskTopContentMaxWidth: string;
    hasWrapperMinHeight?: boolean;
    sectionBannerCard?: boolean;
    type: string | undefined | null;
  }
>`
  flex-direction: column;
  max-width: 400px;
  word-break: break-word;
  white-space: pre-wrap;
  z-index: 1;

  ${({
    deskTopContentMaxWidth,
    hasWrapperMinHeight,
    sectionBannerCard,
    textAlignment,
    theme,
    type,
  }) => css`
    font-size: ${theme.fontSizes.p2};
    font-family: ${theme.fonts.paragraphFont};
    ${type !== 'Text' &&
    css`
      height: 100%;
    `}
    ${theme.media.md.down} {
      ${sectionBannerCard
        ? css`
            max-height: ${hasWrapperMinHeight
              ? SECTION_BANNER_CARD_SIZES.sm
              : ''};
            height: ${hasWrapperMinHeight ? SECTION_BANNER_CARD_SIZES.sm : ''};
          `
        : css`
            max-height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.sm : ''};
            height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.sm : ''};
          `}
      max-width: 100%;
      width: 100%;
      margin: 0 0;
      ${getTextAlignment(getBrandConfig().textAlignmentMobile)}
    }
    ${theme.media.xs.only} {
      ${sectionBannerCard
        ? css`
            max-height: ${hasWrapperMinHeight
              ? SECTION_BANNER_CARD_SIZES.xs
              : ''};
            height: ${hasWrapperMinHeight ? SECTION_BANNER_CARD_SIZES.xs : ''};
          `
        : css`
            max-height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.xs : ''};
            height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.xs : ''};
          `}
    }
    @media (min-width: 991px) and (max-width: 992px) {
      ${sectionBannerCard
        ? css`
            max-height: ${hasWrapperMinHeight
              ? SECTION_BANNER_CARD_SIZES.sm
              : ''};
            height: ${hasWrapperMinHeight ? SECTION_BANNER_CARD_SIZES.sm : ''};
          `
        : css`
            max-height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.sm : ''};
            height: ${hasWrapperMinHeight ? BASE_CARD_SIZES.sm : ''};
          `}
      max-width: 100%;
      width: 100%;
      margin: 0 0;
      ${getTextAlignment(getBrandConfig().textAlignmentMobile)}
    }
    ${theme.media.md.up} {
      max-width: ${deskTopContentMaxWidth ? deskTopContentMaxWidth : '500px'};
      ${getTextAlignment(textAlignment)}
      justify-content: space-between;
      ${type !== 'Text' &&
      css`
        height: 100%;
      `}
    }

    ${theme.media.lg.up} {
      max-width: ${deskTopContentMaxWidth ? deskTopContentMaxWidth : '550px'};
      ${getTextAlignment(textAlignment)}
      justify-content: space-between;
      ${type !== 'Text' &&
      css`
        height: 100%;
      `}
    }
  `}
  ${({ theme, type }) =>
    type === 'MediaBanner' &&
    css`
      ${theme.media.sm.down} {
        height: 100vw;
        max-height: 100vw;
        justify-content: flex-end;
      }
    `}
`;

export const Subheader = styled(Typography)`
  ${({ color, theme }) => css`
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(2)};
    color: ${color};
    font-size: ${theme.fontSizes.h5};
    font-family: ${theme.fontFamilies.italianPlate};
  `}
`;

export const Background = styled(Box)<{
  $backgroundColor: string;
}>`
  height: 100%;
  width: 100%;
  inset: 0;
  position: absolute;
  z-index: ${theme.zIndex.above};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;
export const FlexGrowSpace = styled(Box)`
  // conditionally set flex-grow
  ${theme.media.md.down} {
    flex-grow: 1;
  }
`;

export const CTAWrappersContainer = styled.div<{
  type: Maybe<string>;
  urlDirection?: string;
  urlPosition?: string;
}>`
  width: 100%;
  display: flex;
  ${({ theme }) => css`
    margin-top: ${theme.space.s120};
  `}
  ${({ urlDirection, urlPosition }) =>
    urlDirection === 'column' &&
    css`
      flex-direction: column;
      align-items: ${urlPosition};
    `}
  ${({ urlDirection, urlPosition }) =>
    urlDirection === 'row' &&
    css`
      flex-direction: row;
      justify-content: ${urlPosition};
    `}
    ${({ theme, type, urlDirection }) =>
    type === 'Text' &&
    urlDirection === 'row' &&
    css`
      ${theme.media.md.up} {
        margin-top: ${theme.spacing(4)};
      }
      ${theme.media.md.down} {
        margin-top: ${theme.spacing(3)};
      }
    `}
  ${({ theme, type, urlDirection }) =>
    type === 'Text' &&
    urlDirection === 'column' &&
    css`
      margin-top: ${theme.spacing(3)};
    `}
`;

export const CTA1Wrapper = styled(Box)`
  width: 192px;
  ${theme.media.md.down} {
    width: 100%;
  }
  @media (min-width: 991px) and (max-width: 992px) {
    width: 100%;
  }
`;
export const CTA2Wrapper = styled(Box)<{ urlDirection?: string }>`
  ${({ theme, urlDirection }) =>
    urlDirection === 'column' &&
    css`
      margin-top: ${theme.spacing(2)};
    `}
  ${({ theme, urlDirection }) =>
    urlDirection === 'row' &&
    css`
      margin-left: ${theme.spacing(2)};
    `}
  width: 192px;
  ${theme.media.md.down} {
    width: 100%;
  }
  @media (min-width: 991px) and (max-width: 992px) {
    width: 100%;
  }
`;
export const HeadingWrapper = styled(Box)<{ type: Maybe<string> }>`
  ${({ theme, type }) =>
    type === 'Icon' &&
    css`
      ${theme.media.md.up} {
        p {
          white-space: nowrap;
        }
      }
    `}
  ${({ theme, type }) =>
    type === 'Icon' &&
    css`
      margin-bottom: ${theme.spacing(1)};
    `}
`;

export const Media = styled(Box)<{
  $backgroundAlignment: Maybe<string>;
}>`
  inset: 0;
  object-fit: cover;
  background-position: center;
  position: absolute;

  ${({ $backgroundAlignment, theme }) => css`
    ${theme.media.md.down} {
      object-position: ${$backgroundAlignment || 'center'};
    }
  `}
`;
